import { supabase } from '../lib/supabase';
import { adminUserService } from './adminUserService';

export interface User {
  id: string;
  email: string;
  role?: string;
}

export const authService = {
  // Iniciar sesión
  signIn: async (email: string, password: string): Promise<{ user: User | null; error: string | null }> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        return { user: null, error: error.message };
      }

      // Obtener el rol del usuario desde la tabla admin_users
      let userRole = 'admin';
      if (data.user?.email) {
        const isSuperAdmin = await adminUserService.isSuperAdmin(data.user.email);
        userRole = isSuperAdmin ? 'super_admin' : 'admin';
        
        // Actualizar último login
        await adminUserService.updateLastLogin(data.user.email);
      }

      return {
        user: data.user ? {
          id: data.user.id,
          email: data.user.email || '',
          role: userRole
        } : null,
        error: null
      };
    } catch (error) {
      return { user: null, error: 'Error al iniciar sesión' };
    }
  },

  // Cerrar sesión
  signOut: async (): Promise<{ error: string | null }> => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error: error?.message || null };
    } catch (error) {
      return { error: 'Error al cerrar sesión' };
    }
  },

  // Obtener usuario actual
  getCurrentUser: async (): Promise<User | null> => {
    try {
      // Agregar timeout para evitar esperas infinitas
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout en getCurrentUser')), 3000);
      });

      const userPromise = supabase.auth.getUser();
      
      const { data: { user }, error } = await Promise.race([userPromise, timeoutPromise]) as any;
      
      if (error || !user) {
        console.warn('Error o usuario no encontrado en getCurrentUser:', error);
        return null;
      }

      // Obtener el rol del usuario desde la tabla admin_users con timeout
      let userRole = 'admin';
      if (user.email) {
        try {
          const roleTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout en isSuperAdmin')), 2000);
          });
          
          const rolePromise = adminUserService.isSuperAdmin(user.email);
          const isSuperAdmin = await Promise.race([rolePromise, roleTimeoutPromise]) as boolean;
          userRole = isSuperAdmin ? 'super_admin' : 'admin';
        } catch (roleError) {
          console.warn('Error al obtener rol de usuario, usando rol por defecto:', roleError);
          userRole = 'admin'; // Rol por defecto si hay error
        }
      }

      return {
        id: user.id,
        email: user.email || '',
        role: userRole
      };
    } catch (error) {
      console.error('Error en getCurrentUser:', error);
      return null;
    }
  },

  // Escuchar cambios en la autenticación
  onAuthStateChange: (callback: (user: User | null) => void) => {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        // Obtener el rol del usuario desde la tabla admin_users con timeout
        let userRole = 'admin';
        if (session.user.email) {
          try {
            const roleTimeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('Timeout en isSuperAdmin')), 5000);
            });
            
            const rolePromise = adminUserService.isSuperAdmin(session.user.email);
            const isSuperAdmin = await Promise.race([rolePromise, roleTimeoutPromise]) as boolean;
            userRole = isSuperAdmin ? 'super_admin' : 'admin';
          } catch (roleError) {
            console.warn('Error al obtener rol de usuario en onAuthStateChange, usando rol por defecto:', roleError);
            userRole = 'admin'; // Rol por defecto si hay error
          }
        }

        callback({
          id: session.user.id,
          email: session.user.email || '',
          role: userRole
        });
      } else {
        callback(null);
      }
    });
  },

  // Verificar si el usuario está autenticado
  isAuthenticated: async (): Promise<boolean> => {
    const user = await authService.getCurrentUser();
    return user !== null;
  }
};